import getVisitorGeolocation from '@klv/context/localization/utils';
import { gtmFormEvent } from '@klv/shared/utils/google-tag-manager';
import { windowIsDefined } from '@klv/shared/utils/functions';
import handleErrors from '../shared/handle-errors';
import handleSuccess from '../shared/handle-success';
import type { ISubmitToHubSpot, IFormattedDataHB } from '../types';
import handleHubspotDemoSuccess from './handle-hubspot-demo-success';

// Formats field data to match HubSpot's expected format
const transformFormData = async (data: any) => {
  const klaviyoPagesVisitCount = sessionStorage.getItem('klaviyoPagesVisitCount');
  const locationData = await getVisitorGeolocation();

  const fields = Object.keys(data).map((key) => {
    if (key === 'legalconsentoptions') {
      return {
        name: 'under_marketing_privacy_policy',
        value: data[key] !== undefined ? data[key] : '',
      };
    }
    if (key === 'conversion_page') {
      return {
        name: 'conversion_page',
        value: windowIsDefined() ? window.location.href : '',
      };
    }
    if (klaviyoPagesVisitCount && key === 'web_pages_visited_prior_to_conversion') {
      try {
        const pagesArray = JSON.parse(klaviyoPagesVisitCount);
        return {
          name: 'web_pages_visited_prior_to_conversion',
          value: JSON.stringify(pagesArray),
        };
      } catch (error) {
        console.error('Error parsing klaviyoPagesVisitCount:', error);
      }
    }
    if (key === 'country_indicated_at_signup' && locationData) {
      return { name: 'country_indicated_at_signup', value: locationData.country };
    }
    return {
      name: key,
      value: data[key] !== undefined ? data[key] : '',
    };
  });

  fields.push({
    name: 'under_marketing_privacy_policy',
    value: true,
  });

  return fields;
};

export const submitToHubSpot = async ({ formData, context, settings, setErrors, setSuccess, resetRecaptcha, errorText, setFieldError, optimizely }: ISubmitToHubSpot) => {
  const fieldsData = await transformFormData(formData);

  const hubSpotData: IFormattedDataHB = { fields: fieldsData, context: context };
  const formattedData = { formattedData: hubSpotData, reCaptchaToken: formData['g-recaptcha-response'] };

  try {
    const submissionResponse = await fetch(`/request-mkt/process/demo-request?DEMO_FORMGUID=${process.env.GATSBY_DEMO_FORMGUID}`, {
      method: 'POST',
      body: JSON.stringify(formattedData),
      headers: {
        'content-type': 'application/json',
      },
    });

    const submissionData = await submissionResponse.json();

    if (submissionData.success) {
      resetRecaptcha();
      setSuccess(true);
      handleSuccess(submissionData, 'demo');
      handleHubspotDemoSuccess(submissionData, 'demo');


      optimizely?.track?.('demo complete', undefined, { ...optimizely?.user?.attributes, formComplete: true });

      if (window?.qualified) {
        window.qualified(
          'saveFormData',
          formData,
        );
        window.qualified('emitFormFill', 'custom');
      }

      gtmFormEvent(formData, 'requestDemoHubSpot', 'complete').then(() => {
        if (windowIsDefined()) {
          setTimeout(() => {
            if (settings?.redirectUrl) {
              window.location.assign(settings.redirectUrl);
            }
          }, 500);
        }
      });

      if (!settings?.redirectUrl) {
        setErrors([]);
        setSuccess(true);
      }

      return submissionData;
    }

    handleErrors(
      submissionData?.errors || [],
      setErrors,
      (field, value) => setFieldError(field, value || ''),
      formData,
      'demo',
    );
    resetRecaptcha();

    return false;
  } catch (error) {
    const knownError = error as { code?: string; message?: string; name?: string; stack?: string; status?: string; };
    window?.heap?.track?.('Form Submission Error', {
      code: knownError?.code ?? '',
      emailAddress: formData.email ?? '',
      formName: 'demo',
      host: process.env.GATSBY_HOST ?? '',
      message: knownError?.message ?? '',
      name: knownError?.name ?? '',
      rawJson: JSON.stringify(error) ?? '',
      stack: knownError?.stack ?? '',
      statusCode: knownError?.status ?? '',
    });
    setErrors([errorText.generic]);
    resetRecaptcha();

    return false;
  }
};
